import { InfoWindow, Marker, Polyline } from "@react-google-maps/api";
import MarkerIcon from "assets/gas-marker.svg";
import HomeMarker from "assets/gray-marker.svg";
import TollMarker from "assets/toll-marker.svg";
import FuelOptimizerLayout from "./fuel-optimizer-layout";
import React, { useState, useEffect } from "react";
import FormLayout from "./form-layout";
import FuelPlan from "./fuel-plan";
import InfoWindowContent from "./info-window-content";
import Map from "./map";
import RouteEfficiencySummary from "./route-efficiency-summary";
import RouteSelector from "./route-selector";
import TollInfoWindowContent from "./toll-info-window-content";
import type { FuelPlan as FuelPlanState, NormalizedFuelLocation, NormalizedFuelPlanRoute } from "./services";
import type { GeoPoint, TollLocation } from "./api";

// Custom CSS for InfoWindow - inject into document head
const injectCustomInfoWindowCSS = () => {
    if (typeof document !== "undefined") {
        // Check if style exists already
        if (!document.getElementById("custom-infowindow-styles")) {
            const style = document.createElement("style");
            style.id = "custom-infowindow-styles";
            style.innerHTML = `
        .gm-style-iw-ch {
          padding: 0 !important;
          display: none !important;
        }
        .gm-style-iw-c {
          padding: 0 !important;
        }
        .gm-style-iw-d {
          padding: 0 !important;
          overflow: hidden !important;
        }
        .gm-ui-hover-effect {
          position: absolute !important;
          top: -10px !important;
          right: 0 !important;
          width: 37px !important;
        }
      `;
            document.head.appendChild(style);
        }
    }
};

const FuelOptimizer = () => {
    const [fuelPlan, setFuelPlan] = useState<FuelPlanState | null>(null);
    const [mapRef, setMapRef] = useState<google.maps.Map | null>(null);
    const [markerMap, setMarkerMap] = useState<Record<string, google.maps.Marker>>({});
    const [selectedPlace, setSelectedPlace] = useState<NormalizedFuelLocation | null>(null);
    const [infoOpen, setInfoOpen] = useState(false);
    const [center, setCenter] = useState<GeoPoint>({ lat: 31.7619, lng: -106.485 });
    const [leftPanelRouteIndex, setLeftPanelRouteIndex] = useState(0);
    const [selectedToll, setSelectedToll] = useState<TollLocation | null>(null);
    const [tollInfoOpen, setTollInfoOpen] = useState(false);

    // Inject custom CSS for InfoWindow when component mounts
    useEffect(() => {
        injectCustomInfoWindowCSS();
    }, []);

    // Custom InfoWindow options to fix styling issues
    const infoWindowOptions = {
        pixelOffset: new window.google.maps.Size(0, 0),
        maxWidth: 250,
        disableAutoPan: false,
    };

    // Define route colors for different routes
    const routeColors = [
        "#047857", // Green - primary route
        "#1E40AF", // Blue
        "#B91C1C", // Red
        "#7E22CE", // Purple
        "#C2410C", // Orange
    ];

    // Define opacity levels
    const ACTIVE_OPACITY = 0.9;
    const INACTIVE_OPACITY = 0.4;
    const ACTIVE_WEIGHT = 5;
    const INACTIVE_WEIGHT = 3;

    const markerLoadHandler = (marker: google.maps.Marker, location: NormalizedFuelLocation) => {
        return setMarkerMap((prevState) => ({ ...prevState, [location.loc_id]: marker }));
    };

    const tollMarkerLoadHandler = (marker: google.maps.Marker, toll: TollLocation) => {
        // Create a consistent ID for toll markers that will match when clicked
        const tollId = getTollId(toll);
        console.log(`Toll marker loaded: ${tollId}`);
        return setMarkerMap((prevState) => ({ ...prevState, [tollId]: marker }));
    };

    const markerClickHandler = (event: google.maps.MapMouseEvent, place: NormalizedFuelLocation) => {
        // Remember which place was clicked
        setSelectedPlace(place);
        setSelectedToll(null);

        // Required so clicking a 2nd marker works as expected
        if (infoOpen) setInfoOpen(false);
        if (tollInfoOpen) setTollInfoOpen(false);

        setInfoOpen(true);
    };

    const tollMarkerClickHandler = (event: google.maps.MapMouseEvent, toll: TollLocation) => {
        // Remember which toll was clicked
        const tollId = getTollId(toll);
        console.log(`Toll marker clicked: ${tollId}`);

        setSelectedToll(toll);
        setSelectedPlace(null);

        // Required so clicking a 2nd marker works as expected
        if (infoOpen) setInfoOpen(false);
        if (tollInfoOpen) setTollInfoOpen(false);

        setTollInfoOpen(true);
    };

    const loadHandler = (map: google.maps.Map) => {
        // Store a reference to the google map instance in state
        setMapRef(map);
    };

    const resetFuelPlan = () => {
        setFuelPlan(null);
        setMarkerMap({});
        setSelectedPlace(null);
        setSelectedToll(null);
        setInfoOpen(false);
        setTollInfoOpen(false);
        setCenter({ lat: 31.7619, lng: -106.485 });
        setLeftPanelRouteIndex(0);
    };

    // Determine if we're showing the pro version with multiple routes
    const isProVersion = Array.isArray(fuelPlan);

    // Narrowed array view of the pro/multi routes (empty when a single plan).
    const proRoutes: NormalizedFuelPlanRoute[] = Array.isArray(fuelPlan) ? fuelPlan : [];

    // Get the primary route to display (for the left panel)
    const currentRoute: NormalizedFuelPlanRoute | null = Array.isArray(fuelPlan)
        ? fuelPlan[leftPanelRouteIndex] ?? null
        : fuelPlan;

    // When dropdown selection changes
    const handleLeftPanelRouteChange = (newIndex: number) => {
        setLeftPanelRouteIndex(newIndex);
    };

    // Helper function to generate consistent toll ID
    const getTollId = (toll: TollLocation) => {
        return `toll-${toll.name.replace(/\s+/g, "-")}-${toll.latitude}-${toll.longitude}`;
    };

    return (
        <>
            <div className="w-full h-full">
                <FuelOptimizerLayout
                    leftPanel={
                        !fuelPlan ? (
                            <div className="border-r border-gray-200 bg-white flex flex-col lg:h-full">
                                <div className="bg-white flex-1">
                                    <FormLayout setFuelPlan={setFuelPlan} />
                                </div>
                            </div>
                        ) : (
                            <div className="h-full flex flex-col border-r border-gray-200 bg-white">
                                {isProVersion && (
                                    <RouteSelector
                                        routes={proRoutes}
                                        selectedIndex={leftPanelRouteIndex}
                                        onChange={handleLeftPanelRouteChange}
                                    />
                                )}
                                <div className="lg:flex-grow lg:h-1">
                                    <FuelPlan
                                        fuelPlan={currentRoute!}
                                        setFuelPlan={(updatedRoute) => {
                                            const newFuelPlan = [...proRoutes];
                                            newFuelPlan[leftPanelRouteIndex] = updatedRoute;
                                            setFuelPlan(newFuelPlan);
                                        }}
                                        resetFuelPlan={resetFuelPlan}
                                    />
                                </div>
                            </div>
                        )
                    }
                    map={
                        <div className="flex flex-col h-full overflow-y-auto">
                            {isProVersion && (
                                <RouteEfficiencySummary routes={proRoutes} currentRouteIndex={leftPanelRouteIndex} />
                            )}
                            <div className="relative h-[500px] md:h-[600px] lg:h-auto lg:flex-1">
                                <Map
                                    onLoad={loadHandler}
                                    center={center}
                                    onCenterChanged={() => {
                                        if (mapRef) setCenter(mapRef.getCenter()!.toJSON());
                                    }}
                                    routes={fuelPlan}
                                >
                                    {currentRoute && (
                                        <>
                                            {/* Origin and Destination markers */}
                                            <Marker
                                                position={{
                                                    lat: currentRoute.origin.lat,
                                                    lng: currentRoute.origin.lng,
                                                }}
                                                icon={{
                                                    url: HomeMarker,
                                                    scaledSize: new window.google.maps.Size(30, 30),
                                                }}
                                            />
                                            <Marker
                                                position={{
                                                    lat: currentRoute.destination.lat,
                                                    lng: currentRoute.destination.lng,
                                                }}
                                                icon={{
                                                    url: HomeMarker,
                                                    scaledSize: new window.google.maps.Size(30, 30),
                                                }}
                                            />

                                            {/* ROUTES: Show all routes with current route highlighted */}
                                            {isProVersion ? (
                                                <>
                                                    {/* Show all routes in the Pro version */}
                                                    {proRoutes.map((route, idx) => {
                                                        const routeColor = routeColors[idx];

                                                        // Define if this is the current route selected in dropdown
                                                        const isCurrentRoute = idx === leftPanelRouteIndex;

                                                        return (
                                                            <Polyline
                                                                key={`route-${idx}`}
                                                                path={route.decodedPolyline}
                                                                options={{
                                                                    strokeColor: routeColor,
                                                                    strokeOpacity: isCurrentRoute
                                                                        ? ACTIVE_OPACITY
                                                                        : INACTIVE_OPACITY,
                                                                    strokeWeight: isCurrentRoute
                                                                        ? ACTIVE_WEIGHT
                                                                        : INACTIVE_WEIGHT,
                                                                    fillColor: routeColor,
                                                                    fillOpacity: 0.35,
                                                                    clickable: false,
                                                                    draggable: false,
                                                                    editable: false,
                                                                    visible: true,
                                                                    zIndex: isCurrentRoute ? 2 : 1, // Current route on top
                                                                    // fillColor/fillOpacity are polygon-only and ignored on a
                                                                    // polyline; cast keeps the existing (harmless) shape.
                                                                }as google.maps.PolylineOptions}
                                                            />
                                                        );
                                                    })}
                                                </>
                                            ) : (
                                                // For non-Pro version, just display the single route
                                                <Polyline
                                                    path={currentRoute.decodedPolyline}
                                                    options={{
                                                        strokeColor: routeColors[0],
                                                        strokeOpacity: 0.8,
                                                        strokeWeight: 4,
                                                        fillColor: routeColors[0],
                                                        fillOpacity: 0.35,
                                                        clickable: false,
                                                        draggable: false,
                                                        editable: false,
                                                        visible: true,
                                                        zIndex: 1,
                                                    } as google.maps.PolylineOptions}
                                                />
                                            )}

                                            {/* Fuel Purchase Locations - show only for the current selected route */}
                                            {isProVersion
                                                ? // In Pro version, only show markers for the current selected route
                                                  currentRoute.fuelPurchaseLocations.map((location) => {
                                                      if (!location.active) return null;

                                                      return (
                                                          <React.Fragment key={`marker-${location.loc_id}`}>
                                                              <Marker
                                                                  position={{ lat: location.lat, lng: location.lng }}
                                                                  onLoad={(marker) =>
                                                                      markerLoadHandler(marker, location)
                                                                  }
                                                                  onClick={(event) => {
                                                                      markerClickHandler(event, location);
                                                                  }}
                                                                  icon={{
                                                                      url: MarkerIcon,
                                                                      scaledSize: new window.google.maps.Size(30, 30),
                                                                  }}
                                                              />
                                                              {infoOpen &&
                                                                  selectedPlace?.loc_id === location.loc_id && (
                                                                      <InfoWindow
                                                                          anchor={markerMap[selectedPlace!.loc_id]}
                                                                          onCloseClick={() => setInfoOpen(false)}
                                                                          options={infoWindowOptions}
                                                                      >
                                                                          <InfoWindowContent place={selectedPlace!} />
                                                                      </InfoWindow>
                                                                  )}
                                                          </React.Fragment>
                                                      );
                                                  })
                                                : // For non-Pro version, show markers from the current route
                                                  currentRoute.fuelPurchaseLocations.map((location) => {
                                                      if (!location.active) return null;

                                                      return (
                                                          <React.Fragment key={`marker-${location.loc_id}`}>
                                                              <Marker
                                                                  position={{ lat: location.lat, lng: location.lng }}
                                                                  onLoad={(marker) =>
                                                                      markerLoadHandler(marker, location)
                                                                  }
                                                                  onClick={(event) => {
                                                                      markerClickHandler(event, location);
                                                                  }}
                                                                  icon={{
                                                                      url: MarkerIcon,
                                                                      scaledSize: new window.google.maps.Size(30, 30),
                                                                  }}
                                                              />
                                                              {infoOpen &&
                                                                  selectedPlace?.loc_id === location.loc_id && (
                                                                      <InfoWindow
                                                                          anchor={markerMap[selectedPlace!.loc_id]}
                                                                          onCloseClick={() => setInfoOpen(false)}
                                                                          options={infoWindowOptions}
                                                                      >
                                                                          <InfoWindowContent place={selectedPlace!} />
                                                                      </InfoWindow>
                                                                  )}
                                                          </React.Fragment>
                                                      );
                                                  })}

                                            {/* Toll Locations for both Pro and non-Pro versions */}
                                            {currentRoute.tollLocations &&
                                                currentRoute.tollLocations.length > 0 &&
                                                // Show toll markers for the current route
                                                currentRoute.tollLocations.map((toll, idx) => {
                                                    const tollId = getTollId(toll);
                                                    const isSelected =
                                                        tollInfoOpen &&
                                                        selectedToll &&
                                                        getTollId(selectedToll) === tollId;

                                                    return (
                                                        <React.Fragment key={tollId}>
                                                            <Marker
                                                                position={{ lat: toll.latitude, lng: toll.longitude }}
                                                                onLoad={(marker) => tollMarkerLoadHandler(marker, toll)}
                                                                onClick={(event) => {
                                                                    tollMarkerClickHandler(event, toll);
                                                                }}
                                                                icon={{
                                                                    url: TollMarker,
                                                                    scaledSize: new window.google.maps.Size(30, 40),
                                                                }}
                                                            />
                                                            {isSelected && (
                                                                <InfoWindow
                                                                    anchor={markerMap[tollId]}
                                                                    position={
                                                                        markerMap[tollId]
                                                                            ? undefined
                                                                            : {
                                                                                  lat: selectedToll!.latitude,
                                                                                  lng: selectedToll!.longitude,
                                                                              }
                                                                    }
                                                                    onCloseClick={() => setTollInfoOpen(false)}
                                                                    options={infoWindowOptions}
                                                                >
                                                                    <TollInfoWindowContent toll={selectedToll!} />
                                                                </InfoWindow>
                                                            )}
                                                        </React.Fragment>
                                                    );
                                                })}
                                        </>
                                    )}
                                </Map>
                            </div>
                        </div>
                    }
                />
            </div>
        </>
    );
};

export default FuelOptimizer;
